import React, { useMemo } from 'react';
import {
  PrioritizationExpression,
  PrioritizationMetric,
  PrioritizationComparator,
  PrioritizationMetricSchema,
  PrioritizationComparatorToNameMap,
} from '@scale/shared-types';
import { SelectMenu, Typography, BaseIcon, TextField } from '@scale/scaleui';
import { PrioritizationMetricToConstantInputComponentMap } from 'react-scale/corp/components/QM/pipeline/prioritization/PrioritizationConstantInputs';
import _ from 'lodash';
import Link from 'next/link';

interface PrioritizationExpressionItemProps {
  name: string;
  updateProperty: (arg: Partial<PrioritizationExpression<PrioritizationMetric>>) => void;
  existingValue?: Partial<PrioritizationExpression<PrioritizationMetric>>;
  required?: boolean;
}

const PrioritizationExpressionItem: React.FC<PrioritizationExpressionItemProps> = ({
  name,
  updateProperty,
  existingValue,
}) => {
  const expression = existingValue;
  const ConstantInputComponent = useMemo(() => {
    if (!_.isNil(expression?.input)) {
      return PrioritizationMetricToConstantInputComponentMap[expression.input];
    }
    return null;
  }, [expression?.input]);

  return (
    <div className="scaleui scaleui-tailwind scale-theme">
      {name && <div className="my-2 text-sm font-medium leading-6 text-neutral-700">{name}</div>}
      <Link
        href="https://scale.atlassian.net/wiki/spaces/OP/pages/959348776/Prioritization+Builder#Metric-Definitions"
        className="items-center inline-flex"
        target="_blank"
        rel="noopener noreferrer"
      >
        <Typography.Paragraph3 className="text-primary-600 flex items-center ">
          View Metric Definitions
        </Typography.Paragraph3>
        <BaseIcon icon={['far', 'external-link-alt']} variant="primary" />
      </Link>
      {!_.isNil(expression) && (
        <div className="flex gap-2">
          <SelectMenu
            className="flex-shrink-1 min-w-[135px]"
            options={Object.values(PrioritizationMetric).map(metric => ({
              id: String(metric),
              name: PrioritizationMetricSchema[metric].name,
            }))}
            selectedOptionId={String(expression.input)}
            setSelected={value => {
              updateProperty({
                ...expression,
                input: value as PrioritizationMetric,
                comparator:
                  value !== expression.input
                    ? PrioritizationMetricSchema[value as PrioritizationMetric].comparators[0]
                    : expression.comparator,
                constant: value !== expression.input ? undefined : expression.constant,
              });
            }}
            placeholder="Select Metric"
          />
          {!_.isNil(expression.input) ? (
            <SelectMenu
              className="flex-shrink-1 min-w-[60px]"
              options={PrioritizationMetricSchema[expression.input].comparators.map(comparator => ({
                id: String(comparator),
                name: PrioritizationComparatorToNameMap[comparator],
              }))}
              selectedOptionId={String(expression.comparator)}
              setSelected={value => {
                updateProperty({
                  ...expression,
                  comparator: value as PrioritizationComparator,
                });
              }}
            />
          ) : (
            <SelectMenu
              className="flex-shrink-1 min-w-0"
              options={[{ id: 'placeholder', name: 'Select Comparator' }]}
              selectedOptionId={'placeholder'}
              setSelected={() => {}}
            />
          )}
          {!_.isNil(ConstantInputComponent) ? (
            <div className="flex-shrink-1 min-w-0">
              <ConstantInputComponent
                setValue={(value: any) => {
                  updateProperty({ ...expression, constant: value });
                }}
                value={expression.constant}
              />
            </div>
          ) : (
            <div className="flex-shrink-1 min-w-0">
              <TextField placeholder="Constant" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PrioritizationExpressionItem;
